import { useLayoutEffect, useRef, useState } from 'react';

let canvas: HTMLCanvasElement | null = null;

/** One shared canvas context; null when the browser has none (tests). */
function measureContext(): CanvasRenderingContext2D | null {
  canvas ??= document.createElement('canvas');
  return canvas.getContext('2d');
}

/**
 * Text that cuts itself to fit its box using `fit` (see `fitText.ts`). The full
 * text stays in `title` and for screen readers. The invisible sizer keeps the
 * box as wide as the full text wants, so a cut text never shrinks its own box
 * and the text grows back when the box widens.
 */
export function FittedText({
  text,
  fit,
  className,
}: {
  text: string;
  fit: (fits: (candidate: string) => boolean) => string;
  className?: string;
}) {
  const box = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState(text);
  // The caller builds `fit` inline, so keep the newest one without re-observing.
  const fitRef = useRef(fit);
  fitRef.current = fit;

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;

    const measure = () => {
      const ctx = measureContext();
      const width = el.clientWidth;
      if (!ctx || width === 0) {
        setShown(text);
        return;
      }
      // clientWidth is rounded, so a text that fits can measure a fraction wider.
      if (el.scrollWidth <= el.clientWidth) {
        setShown(text);
        return;
      }
      const style = getComputedStyle(el);
      ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      setShown(
        fitRef.current(candidate => ctx.measureText(candidate).width <= width)
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // Web fonts change the widths once they load.
    document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [text]);

  return (
    <span
      ref={box}
      title={text}
      className={`relative block min-w-0 overflow-hidden whitespace-nowrap ${className ?? ''}`}
    >
      <span aria-hidden='true' className='invisible'>
        {text}
      </span>
      <span aria-hidden='true' className='absolute inset-0 truncate'>
        {shown}
      </span>
      <span className='sr-only'>{text}</span>
    </span>
  );
}
